import { getCookie, deleteCookie, buildRequest } from "./auth.js";
import UnauthorisedError from '../errors/UnauthorisedError.js';

export async function fetchEyeTicketById(id) {
    const url = `/EyeTicket/GetEyeTicketById/${id}`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            } else {
                throw new Error(`Failed to fetch EyeTicket: ${response.status} ${response.statusText}`);
            }
        }

        const data = await response.json();
        return data[0];
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchOpticiansEyeTicketsAwaitingProcessing(page, pageSize, searchValue = null, barcode = null) {
    let url = `/EyeTicketProcessing/GetEyeTicketsAwaitingProcessing/${encodeURIComponent(pageSize)}/${encodeURIComponent(page)}`;

    if (barcode || searchValue) {
        url += '?';

        if (barcode) {
            url += `cSearchBarcode=${encodeURIComponent(barcode)}`;
        }

        if (searchValue) {
            if (barcode) {
                url += '&';
            }
            url += `cNameSearchTerm=${encodeURIComponent(searchValue)}`;
        }
    }

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 404) {
                console.warn('No EyeTickets found for the given criteria.');
                return [];
            }
            else if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch EyeTickets: ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchOpticiansEyeTicketsAwaitingProcessingAdvancedSearch(surname = null, barcode = null) {
    if (!surname || !barcode) {
        return [];
    }
    let url = `/Optician/AdvancedEyeTicketRecall/10/1/${encodeURIComponent(barcode)}/${encodeURIComponent(surname)}`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 404) {
                console.warn('No EyeTickets found for the given criteria.');
                return [];
            }
            else if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch EyeTickets: ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchOpticiansEyeTickets(page, pageSize, searchValue = null, status = null) {
    let url = `/Optician/GetOpticiansEyeTicketsHistory/${encodeURIComponent(pageSize)}/${encodeURIComponent(page)}`;

    if (status || searchValue) {
        url += '?';

        if (status) {
            url += `cStatus=${encodeURIComponent(status)}`;
        }

        if (searchValue) {
            if (status) {
                url += '&';
            }
            url += `cGlobalSearch=${encodeURIComponent(searchValue)}`;
        }
    }

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 404) {
                console.warn('No EyeTickets found for the given criteria.');
                return [];
            }
            else if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch EyeTickets: ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchTicketStatuses() {
    const url = `/EyeTicket/GetEyeTicketStatuses`;

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            } else {
                throw new Error(`Failed to fetch EyeTickets Statuses: ${response.status} ${response.statusText}`);
            }
        }

        const data = await response.json();

        const transformedData = data.map(item => {
            const [name, value] = item.split(',').map(str => str.trim());
            return { name, value };
        });

        return transformedData;
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function fetchUsersEyeTickets(account, page, pageSize, status = null, searchValue = null, raisedBy = null) {
    let url = `/EndUser/GetEndUserEyeTicketsHistory/${encodeURIComponent(account)}/${encodeURIComponent(pageSize)}/${encodeURIComponent(page)}`;

    if (status || searchValue || raisedBy) {
        url += '?';

        if (status) {
            url += `cStatus=${encodeURIComponent(status)}`;
        }

        if (searchValue) {
            if (status) {
                url += '&';
            }
            url += `cSearchValue=${encodeURIComponent(searchValue)}`;
        }

        if (raisedBy) {
            if (status || searchValue) {
                url += '&';
            }
            url += `cRaisedBy=${encodeURIComponent(raisedBy)}`;
        }
    }

    try {
        const response = await buildRequest(url);

        if (!response.ok) {
            if (response.status === 404) {
                console.warn('No EyeTickets found for the given criteria.');
                return [];
            }
            else if (response.status === 401) {
                deleteCookie();
                throw new UnauthorisedError();
            }
            else {
                throw new Error(`Failed to fetch EyeTickets: ${response.status} ${response.statusText}`);
            }
        }

        return await response.json();
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function updateEyeTicketStatus(eyeTicketId, status) {
    const url = `/EyeTicket/UpdateEyeTicketStatus/${encodeURIComponent(eyeTicketId)}?cStatus=${encodeURIComponent(status)}`

    try {
        const response = await buildRequest(url, 'PUT');
        const text = await response.text();
        try {
            const data = JSON.parse(text);
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else {
                    throw new Error(`Failed to update EyeTicket status: ${response.status} ${response.statusText} | ${data.detail || text}`);
                }
            }
            return data;
        } catch (err) {
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else {
                    throw new Error(`Failed to update EyeTicket status: ${response.status} ${response.statusText} | ${text}`);
                }
            }
        }
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function setEyeTicketDailyOrderTracking(eyeTicketId, enabled) {
    const url = `/EndUser/SetEyeTicketDailyTracking/${encodeURIComponent(eyeTicketId)}?lTrackOrder=${encodeURIComponent(enabled)}`

    try {
        const response = await buildRequest(url, 'PUT');
        const text = await response.text();
        try {
            const data = JSON.parse(text);
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else {
                    throw new Error(`Failed to update EyeTicket daily tracking: ${response.status} ${response.statusText} | ${data.detail || text}`);
                }
            }
            return data;
        } catch (err) {
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else {
                    throw new Error(`Failed to update EyeTicket daily tracking: ${response.status} ${response.statusText} | ${text}`);
                }
            }
        }
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function resendEyeTicketNotifications(eyeTicketId) {
    const url = `/EndUser/ResendCommunication/?iEticketUnid=${encodeURIComponent(eyeTicketId)}`

    try {
        const response = await buildRequest(url, 'POST');
        const text = await response.text();
        try {
            const data = JSON.parse(text);
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else {
                    throw new Error(`Failed to resend EyeTicket notifications: ${response.status} ${response.statusText} | ${data.detail || text}`);
                }
            }
            return data;
        } catch (err) {
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else {
                    throw new Error(`Failed to resend EyeTicket notifications: ${response.status} ${response.statusText} | ${text}`);
                }
            }
        }
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function updateEyeTicket(eyeTicket, eyeTicketId) {
    const url = `/EyeTicket/UpdateEyeTicket/${encodeURIComponent(eyeTicketId)}`;

    try {
        const response = await buildRequest(url, 'PUT', eyeTicket);
        const text = await response.text();
        try {
            const data = JSON.parse(text);
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else {
                    throw new Error(`Failed to update EyeTicket: ${response.status} ${response.statusText} | ${data.detail || text}`);
                }
            }
            return data;
        } catch (err) {
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else {
                    throw new Error(`Failed to update EyeTicket: ${response.status} ${response.statusText} | ${text}`);
                }
            }
        }
    } catch (error) {
        throw new Error(error.message);
    }
}

export async function createEyeTicket(eyeTicket, override = false, orderNoPrefix = null) {
    const url = (
        override ? `/EyeTicket/PostNewEyeTicket/true`
            : `/EyeTicket/PostNewEyeTicket/false`);


    if (orderNoPrefix && orderNoPrefix !== "-") {
        const orderNo = eyeTicket.eticketorderno.toString();
        const newOrderNo = `${orderNoPrefix.toString()}${orderNo}`;

        eyeTicket.eticketorderno = newOrderNo;
    }

    try {
        const response = await buildRequest(url, 'POST', eyeTicket);
        const text = await response.text();
        try {
            const data = JSON.parse(text);
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else if (text.includes("Has Had An EyeTicket Raised Within Your Recall Period")) {
                    return "Duplicate Clock No";
                }
                else {
                    throw new Error(`Failed to create EyeTicket: ${response.status} ${response.statusText} | ${data.detail || text}`);
                }
            }
            return data;
        } catch (err) {
            if (!response.ok) {
                if (response.status === 401) {
                    deleteCookie();
                    throw new UnauthorisedError();
                }
                else if (text.includes("Has Had An EyeTicket Raised Within Your Recall Period")) {
                    return "Duplicate Clock No";
                }
                else {
                    throw new Error(`Failed to create EyeTicket: ${response.status} ${response.statusText} | ${text}`);
                }
            }
        }

       if (response.ok && response.status === 200) {
            const barcodeMatch = text.match(/New EyeTicket ID \d+ raised successfully for barcode No\. (\d+)/i);
            if (barcodeMatch && barcodeMatch[1]) {
                return parseInt(barcodeMatch[1], 10);
            }

            const eyeTicketMatch = text.match(/New EyeTicket ID (\d+) raised successfully/i);
            if (eyeTicketMatch && eyeTicketMatch[1]) {
                return parseInt(eyeTicketMatch[1], 10);
            }
            throw new Error(`Failed to parse Barcode or EyeTicket ID from response: ${text}`);
        }
        
    } catch (error) {
        throw new Error(error.message);
    }
}
